import React, {useEffect, useState} from 'react';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardActions from '@material-ui/core/CardActions';
import TextField from '@material-ui/core/TextField';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';

import withStyle from '../style';

const defaultFields = ['amountLeft','amountPoured'];

export default function MeasurementEditor({ measurement, measurements, onEdit }) {
  const { _id, mac, measurementTimeUTC, measuredData, productId } = measurement;

  const [fields, setFields] = useState([]);
  const [fp, setFp] = useState(undefined);
  const [bg, setBg] = useState(false);
  const [actualAmountPoured, setActualAmountPoured] = useState("");
  const [actualAmountLeft, setActualAmountLeft] = useState("");

  const [hasInteractedPoured, setHasInteractedPoured] = useState(false);
  const [hasInteractedLeft, setHasInteractedLeft] = useState(false);
  const [showAutoCompute, setShowAutoCompute] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  const prod = productId;
  const classes = withStyle();

  useEffect(() => {
    if (!hasInteractedPoured) return;
    const delay = setTimeout(() => {
      if (actualAmountPoured !== "") {
        const val = parseFloat(actualAmountPoured);
        if (!isNaN(val)) {
          onEdit("actualAmountPoured", val);
          setHasInteractedPoured(false);
        }
      }
    }, 1000);
    return () => clearTimeout(delay);
  }, [actualAmountPoured, hasInteractedPoured, onEdit]);

  useEffect(() => {
    if (!hasInteractedLeft) return;
    const delay = setTimeout(() => {
      if (actualAmountLeft !== "") {
        const val = parseFloat(actualAmountLeft);
        if (!isNaN(val)) {
          onEdit("actualAmountLeft", val);
          setHasInteractedLeft(false);
        }
      }
    }, 1000);
    return () => clearTimeout(delay);
  }, [actualAmountLeft, hasInteractedLeft, onEdit]);

  useEffect(() => {
    const left = parseFloat(actualAmountLeft);
    const pouredExists = measuredData?.actualAmountPoured !== undefined;
    setShowAutoCompute(!isNaN(left) && !pouredExists);
  }, [actualAmountLeft, measuredData]);

  useEffect(() => {
    const keys = Object.keys(Object.assign({}, measuredData)).filter(
      key => !['actualAmountPoured', 'actualAmountLeft'].includes(key)
    );
    const needsKeys = !keys.every(elem => fields.includes(elem));
    if (needsKeys) {
      defaultFields.forEach(field => {
        if (!keys.includes(field)) keys.push(field);
      });
      setFields(keys.sort());
    }
  
    setFp(measuredData?.falsePositive ?? undefined);
  
    if (
      (actualAmountPoured === '' || actualAmountPoured === null) &&
      (measuredData?.actualAmountPoured !== undefined)
    ) {
      setActualAmountPoured(String(measuredData.actualAmountPoured));
    }
  
    if (
      (actualAmountLeft === '' || actualAmountLeft === null) &&
      (measuredData?.actualAmountLeft !== undefined)
    ) {
      setActualAmountLeft(String(measuredData.actualAmountLeft));
    }
  }, [measuredData, fields, actualAmountPoured, actualAmountLeft]);

  useEffect(() => {
    setBg(fp ? "#e553" : "#fff");
  }, [fp]);

  function findLatestPreviousActualAmountLeft() {
    // console.log('--- Running findLatestPreviousActualAmountLeft ---');
    // console.log('Target mac:', measurement.mac);
    // console.log('Target productId:', measurement.productId?._id);
    // console.log('current _id:', measurement._id);

    const filtered = measurements.filter(m => {
      const sameMac = m.mac === measurement.mac;
      const sameProductId = m.productId?._id === measurement.productId?._id;
      const notSameId = m._id !== measurement._id;
      const hasLeft = m.measuredData?.actualAmountLeft !== undefined;
  
      const valid = sameMac && sameProductId && notSameId && hasLeft;
      if (!valid) {
        console.log('Skipping:', {
          mac: m.mac,
          productId: m.productId?._id,
          _id: m._id,
          actualAmountLeft: m.measuredData?.actualAmountLeft,
        });
      }
      return valid;
    });
  
    // console.log('Filtered entries count:', filtered.length);
    filtered.forEach((m, i) => {
      console.log(`Filtered[${i}]:`, {
        _id: m._id,
        time: m.measurementTimeUTC,
        actualAmountLeft: m.measuredData.actualAmountLeft
      });
    });
  
    const entries = filtered.sort((a, b) => new Date(b.measurementTimeUTC) - new Date(a.measurementTimeUTC));
    const latest = parseFloat(entries[0]?.measuredData?.actualAmountLeft);
    console.log('Latest parsed actualAmountLeft:', latest);
  
    return latest || NaN;
  }

  return (
    <Card
      className={classes.volumetricCard}
      style={{
        backgroundColor: bg,
      }}>
      <CardHeader
        avatar={
          <Avatar alt={prod.name} src={prod.iconURL} className={classes.bigAvatar}>
            {prod.name.substr(0, 1)}
          </Avatar>
        }
        title={
          <Typography variant="h5">
            {prod.name}{' '}
            <Typography component="span" variant="body2" color="textSecondary">
              ({prod.productId})
            </Typography>
          </Typography>
        }
        subheader={
          <Box mt={1.5}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Label ID: {mac}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Measurement ID: {_id}
              <Tooltip title="View full measurement object">
                <IconButton size="small" onClick={handleOpenDialog} style={{ marginLeft: 4 }}>
                  <InfoIcon fontSize="medium" />
                </IconButton>
              </Tooltip>
              {measuredData?.falsePositive ? " (false positive)" : ""}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Measurement Time (Local): {new Date(measurementTimeUTC).toLocaleString()}
            </Typography>
          </Box>
        }
      />
        
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Measurement Object</DialogTitle>
        <DialogContent dividers>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 14 }}>
            {JSON.stringify(
              {
                _id,
                measuredData: measurement.measuredData,
                data: measurement.data,
                computedData: measurement.computedData,
                hiddenComputedData: measurement.hiddenComputedData
              },
              null,
              2
            )}
          </pre>
        </DialogContent>
        {/* <DialogContent dividers>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 14 }}>
            {JSON.stringify(measurement, null, 2)}
          </pre>
        </DialogContent> */}
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <CardActions>
        {/* {fields
          .sort()
          .filter(e => !['falsePositive', 'actualAmountPoured', 'actualAmountLeft'].includes(e))
          .map((name) => {
            return (
              <TextField
                label={name}
                key={name}
                id={name + measurement._id}
                variant="filled"
                onChange={(e) => {
                  e.preventDefault();
                  onEdit(name, e.target.value);
                }}
                onFocus={(e) => {
                  e.target.select();
                }}
                defaultValue={(measuredData || {})[name] ?? ""}></TextField>
            );
          })} */}

        <Grid container spacing={2}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={1} direction="column">
                <Grid item>
                  <Button onClick={() => {
                    const hasAmount = fields.includes('amountLeft');
                    if (hasAmount) {
                      onEdit('amountLeft', 0.0);
                    } else {
                      setFields([...fields, 'amountLeft']);
                    }
                  }}>
                    Reset Measured Amount
                  </Button>
                </Grid>
                <Grid item>
                  <Button onClick={() => {
                    const hasAmount = fields.includes('amountPoured');
                    if (hasAmount) {
                      onEdit('amountPoured', 0.0);
                    } else {
                      setFields([...fields, 'amountPoured']);
                    }
                  }}>
                    Reset Poured Amount
                  </Button>
                </Grid>
                <Grid item>
                  {fp !== 1 && (
                    <Button onClick={() => {
                      setFp(1);
                      onEdit('falsePositive', 1);
                    }}>
                      Mark False Positive
                    </Button>
                  )}
                </Grid>
                <Grid item>
                  {fp === 1 && (
                    <Button onClick={() => {
                      setFp(undefined);
                      onEdit('falsePositive', 0);
                    }}>
                      Mark True Positive
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Grid>

            {/* Right Column */}
            {measurement?.data?.forced !== true && (
              <Grid item xs={12} md={6}>
                <Grid container spacing={1} direction="column">
                  <Grid item>
                    <TextField
                      label="actualAmountPoured"
                      type="number"
                      variant="filled"
                      value={actualAmountPoured}
                      onChange={(e) => {
                        setHasInteractedPoured(true);
                        setActualAmountPoured(e.target.value);
                      }}
                      fullWidth
                    />
                    {showAutoCompute && (
                      <Button
                        size="small"
                        onClick={() => {
                          const latest = findLatestPreviousActualAmountLeft();
                          console.log("Latest previous actualAmountLeft:", latest);
                          const current = parseFloat(actualAmountLeft);
                          console.log("Current actualAmountLeft:", current);
                          if (!isNaN(current) && !isNaN(latest)) {
                            const poured = latest - current;
                            setActualAmountPoured(poured);
                            console.log("Computed actualAmountPoured:", poured);
                            setHasInteractedPoured(true);
                            setShowAutoCompute(false);
                          }
                        }}
                        style={{ marginTop: 4 }}
                      >
                        Auto-compute Poured
                      </Button>
                    )}
                  </Grid>
                  <Grid item>
                    <TextField
                      label="actualAmountLeft"
                      type="number"
                      variant="filled"
                      value={actualAmountLeft}
                      onChange={(e) => {
                        setHasInteractedLeft(true);
                        setActualAmountLeft(e.target.value);
                      }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
      </CardActions>
    </Card>
  );
}
